// StudyDost — Teacher Onboard Page v2
// frontend/src/pages/TeacherOnboard.js
// Route: /teacher

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import api from '../services/api';

const BOARDS = ['BSEB', 'CBSE', 'UP', 'ICSE', 'OTHER'];
const SUBJECTS = ['Math', 'Science', 'Physics', 'Chemistry', 'Biology', 'English', 'Hindi', 'Social Science', 'Computer'];
const STATES = ['Bihar', 'Uttar Pradesh', 'Madhya Pradesh', 'Rajasthan', 'West Bengal', 'Maharashtra', 'Other'];

export default function TeacherOnboard() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [step, setStep] = useState(user ? 'profile' : 'phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [profile, setProfile] = useState({
    name: user?.name || '',
    schoolName: '',
    schoolCity: '',
    schoolState: 'Bihar',
    board: 'BSEB',
    subjects: [],
    qualification: '',
  });

  const toggleSubject = (s) => {
    setProfile(p => ({
      ...p,
      subjects: p.subjects.includes(s)
        ? p.subjects.filter(x => x !== s)
        : [...p.subjects, s],
    }));
  };

  const handleSendOTP = async () => {
    if (phone.length < 10) return;
    setLoading(true);
    setError('');
    try {
      await authAPI.sendOTP(phone);
      setOtpSent(true);
    } catch {
      setError('Failed to send OTP. Please try again.. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      // Pass requestedRole: TEACHER so backend assigns TEACHER role
      const res = await authAPI.verifyOTP(phone, otp, undefined, undefined, 'TEACHER');
      const { token, user: userData } = res.data;
      // Store token correctly and update auth context
      login(token, userData);
      setProfile(p => ({ ...p, name: userData.name || '' }));
      setStep('profile');
    } catch {
      setError('Incorrect OTP. Please try again.. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProfile = async () => {
    if (!profile.schoolName) { setError('School ka naam zaroori hai'); return; }
    if (profile.subjects.length === 0) { setError('Kam se kam ek subject select karein'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/teacher/onboard', profile);
      navigate('/teacher/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Onboarding fail ho gayi. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src="/studydost_logo.png" alt="StudyDost" style={{ height: 40, width: "auto", marginBottom: 4 }} />
        <h1 style={styles.title}>Teacher Portal</h1>
        <p style={styles.subtitle}>Register karein aur apni class manage karein</p>

        {/* Phone / OTP step */}
        {step === 'phone' && (
          <div style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Phone Number</label>
              <div style={styles.phoneRow}>
                <span style={styles.countryCode}>🇮🇳 +91</span>
                <input
                  style={styles.input}
                  type="tel"
                  placeholder="10-digit number"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  inputMode="numeric"
                />
              </div>
            </div>

            {otpSent && (
              <div style={styles.field}>
                <label style={styles.label}>OTP (6-digit)</label>
                <input
                  style={{ ...styles.input, textAlign: 'center', letterSpacing: '0.3em', fontSize: 22, fontWeight: 700 }}
                  type="tel"
                  placeholder="• • • • • •"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoFocus
                />
              </div>
            )}

            {error && <p style={styles.error}>{error}</p>}

            {!otpSent ? (
              <button
                style={{ ...styles.btn, opacity: phone.length === 10 ? 1 : 0.5 }}
                onClick={handleSendOTP}
                disabled={loading || phone.length !== 10}
              >
                {loading ? 'Bhej rahe hain...' : 'Send OTP'}
              </button>
            ) : (
              <>
                <button
                  style={{ ...styles.btn, opacity: otp.length === 6 ? 1 : 0.5 }}
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? 'Verify ho raha hai...' : 'Verify & Continue'}
                </button>
                <button style={styles.resendBtn} onClick={() => { setOtp(''); handleSendOTP(); }}>
                  OTP nahi aaya? Dobara bhejo
                </button>
              </>
            )}
          </div>
        )}

        {/* Profile step */}
        {step === 'profile' && (
          <div style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Aapka Naam *</label>
              <input style={styles.input} placeholder="Full name" value={profile.name}
                onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>School ka Naam *</label>
              <input style={styles.input} placeholder="e.g. Rajkiya Madhya Vidyalaya" value={profile.schoolName}
                onChange={e => setProfile(p => ({ ...p, schoolName: e.target.value }))} />
            </div>

            <div style={styles.row2}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>City</label>
                <input style={styles.input} placeholder="City" value={profile.schoolCity}
                  onChange={e => setProfile(p => ({ ...p, schoolCity: e.target.value }))} />
              </div>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>State</label>
                <select style={styles.input} value={profile.schoolState}
                  onChange={e => setProfile(p => ({ ...p, schoolState: e.target.value }))}>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Board</label>
              <div style={styles.chipRow}>
                {BOARDS.map(b => (
                  <button key={b}
                    style={{ ...styles.chip, background: profile.board === b ? '#4F46E5' : '#F1F5F9', color: profile.board === b ? '#FFF' : '#374151' }}
                    onClick={() => setProfile(p => ({ ...p, board: b }))}>
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Aap kya padhate hain? *</label>
              <div style={styles.chipRow}>
                {SUBJECTS.map(s => (
                  <button key={s}
                    style={{ ...styles.chip, background: profile.subjects.includes(s) ? '#4F46E5' : '#F1F5F9', color: profile.subjects.includes(s) ? '#FFF' : '#374151' }}
                    onClick={() => toggleSubject(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Qualification (optional)</label>
              <input style={styles.input} placeholder="e.g. B.Ed, M.Sc" value={profile.qualification}
                onChange={e => setProfile(p => ({ ...p, qualification: e.target.value }))} />
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <button style={styles.btn} onClick={handleSubmitProfile} disabled={loading}>
              {loading ? 'Register ho raha hai...' : '🎓 Teacher ke roop mein Register Karein'}
            </button>

            <p style={styles.note}>
              ✅ Aapko ek referral code milega — students use karke 1 month FREE paayenge!
            </p>
          </div>
        )}

        <div style={styles.footer}>
          <a href="/login" style={styles.footerLink}>Student? Yahan login karein →</a>
        </div>
      </div>

      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(160deg, #0F172A, #1E3A5F)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'DM Sans', sans-serif" },
  card: { background: '#FFFFFF', borderRadius: 24, padding: '32px 24px', maxWidth: 480, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' },
  logo: { fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 800, color: '#4F46E5', marginBottom: 8 },
  title: { fontFamily: "'Sora', sans-serif", fontSize: 26, fontWeight: 800, color: '#0F172A', marginBottom: 6 },
  subtitle: { fontSize: 14, color: '#64748B', marginBottom: 28, lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: '#374151' },
  phoneRow: { display: 'flex', border: '2px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' },
  countryCode: { padding: '12px', background: '#F8FAFF', fontSize: 14, borderRight: '2px solid #E2E8F0', whiteSpace: 'nowrap' },
  input: { width: '100%', border: '2px solid #E2E8F0', borderRadius: 12, padding: '12px 14px', fontSize: 15, outline: 'none', fontFamily: "'DM Sans', sans-serif", background: '#FFFFFF' },
  row2: { display: 'flex', gap: 12 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: { border: 'none', borderRadius: 100, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s' },
  btn: { background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', color: '#FFFFFF', border: 'none', borderRadius: 12, padding: '14px', fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 4 },
  resendBtn: { background: 'none', border: 'none', color: '#4F46E5', fontSize: 14, cursor: 'pointer', textDecoration: 'underline', padding: '4px 0', textAlign: 'center' },
  error: { fontSize: 13, color: '#E11D48', background: '#FFF1F2', padding: '8px 12px', borderRadius: 8 },
  note: { fontSize: 12, color: '#64748B', textAlign: 'center', lineHeight: 1.5 },
  footer: { marginTop: 24, textAlign: 'center' },
  footerLink: { fontSize: 13, color: '#4F46E5', textDecoration: 'none', fontWeight: 600 },
};
